// analytics.js - What the site measures, and the one place that decides whether
// it may measure anything at all.
//
// Two destinations, one list of events. The Google tag is hard coded in
// index.html and loaded before this module exists, so there is deliberately no
// measurement id here and no second tag; a smoke check fails if an id appears in
// this file. OpenPanel is our own analytics and is loaded here, after the app
// starts, so it never slows the first paint or touches prerendered HTML.
//
// Every event goes to both. One name, one call site, two destinations, because a
// second module naming the same events is a second place for them to drift.
// OpenPanel adds three things the tag cannot: route changes and outgoing links
// counted without being asked, a `data-track="name"` attribute that makes any
// link or button measurable with no code, and a device id that lets the server
// put a parcel arriving weeks later on the visit that caused it.
//
// Events are deliberately few and obvious. Each one is a row somebody has to
// find in a list in Google Analytics at four in the afternoon, so the list below
// is the list, and `generate_lead` is the conversion.
//
// Page views are not here on purpose. Analytics counts them itself as the URL
// changes, through its own enhanced measurement, and a second page view sent
// from this app would count every route change twice.
//
// Nothing here loads a script. An opted out visitor's tag was never configured,
// so it has sent nothing and will send nothing; the checks are repeated here
// because this module can be called by a page that was already open when
// somebody opted out.
//
// LEGEND
//   1. allowed()       Global Privacy Control, Do Not Track, and our own opt out
//   2. EVENTS, track() the whole vocabulary, and the one way to send it
//   3. the events      one named function per thing worth counting
//   4. context()       the attribution fields the kit form posts
//   5. optOut/optIn    the control on the privacy page
//   6. OpenPanel       loading it, and the device id the server joins on

const OPT_OUT_KEY = "igs.donotshare";
const STASH_KEY = "igs.arrival";

const browser = () => typeof window !== "undefined";

////////// 1. MAY WE //////////
// The same checks run inline in index.html, before the tag is configured. These
// are the copy for a tab that was already open.

const signalsOptOut = () =>
  navigator.globalPrivacyControl === true
  || navigator.doNotTrack === "1"
  || window.doNotTrack === "1"
  || navigator.msDoNotTrack === "1";

export function allowed() {
  if (!browser()) return false;
  if (signalsOptOut()) return false;
  try {
    if (localStorage.getItem(OPT_OUT_KEY) === "1") return false;
  } catch {
    // Storage blocked, which is common in a private window. Treat it as no
    // stored preference rather than as an error.
  }
  return true;
}

////////// 2. THE VOCABULARY //////////
// A name here is an event name in Google Analytics, so renaming one renames it
// there, and anything built on it stops working with nothing to say so.
//
// `generate_lead` is Google's own recommended name for a lead form submission,
// which is why it is used rather than something of our own: Analytics and Google
// Ads both already understand it, so it can be marked as a key event and
// imported as the Ads conversion without any mapping.

export const EVENTS = {
  // --- intent, on the way in. Cheap to earn, worth bidding on once there are
  // enough of them, and worth having as audiences long before that.
  // Somebody looking at what metal is worth today.
  viewPrices: "view_prices",
  // Somebody reading a guide at length, which is the slowest and most
  // deliberate signal on the site.
  viewGuide: "view_guide",
  // A calculator or converter actually used, rather than just looked at.
  toolUsed: "tool_used",
  // A free price watch created.
  priceAlert: "price_alert_created",

  // --- the kit form, which is the whole point of the site
  // The form page opened. Viewing is not starting: most of the drop off is
  // between these first two.
  formView: "form_view",
  // The first time they actually touched it, a tile or a field.
  formStart: "form_start",
  // They reached a later screen. Forward progress only.
  formStep: "form_step",
  // The optional item detail panel opened. Nothing on the form asks for it, so
  // opening it is somebody deciding to tell us more than they had to.
  formItems: "form_items_opened",
  // A request that reached the server and came back with an order number.
  // This is the conversion everything else is judged against.
  lead: "generate_lead",

  // --- reaching a person, which is a lead by any other route
  // A tap on our telephone number. Not a call: see the note in section 3.
  phoneCall: "phone_call",
  // The contact form.
  contact: "contact",
  // A seat at a gold show, which is somebody planning to carry valuables to a
  // hotel on a particular afternoon.
  registerShow: "register_show",

  // --- after the request, on the customer's own tracking page
  // The offer accepted. The money moment, and the one event carrying a value.
  offerAccepted: "offer_accepted",
  // How they want to be paid, which completes the order.
  payoutSubmitted: "payout_submitted",
  // Cover above the standard amount asked for. An existing customer: reported,
  // never bid on.
  coverageRequested: "coverage_requested",

  // --- not a customer at all
  // A shop owner applying to the partner programme. Its own campaign and its
  // own audience, and never mixed into the cost per kit request.
  affiliateApplied: "affiliate_applied",
};

export function track(event, params = {}) {
  if (!browser() || !allowed()) return;
  // The tag is configured in index.html. If it was never configured, because
  // this is the dashboard or somebody opted out, gtag is still defined and
  // still inert, and this call goes nowhere. That is the intended behaviour.
  window.gtag?.("event", event, params);
  // OpenPanel may still be downloading, so early events wait in a queue rather
  // than being lost. Section 6.
  toPanel((panel) => panel.track(event, params));
}

////////// 3. THE EVENTS //////////
// `form_name` is on all three form events so one form can be the conversion
// while the rest stay reportable. The kit form and the counter card form are
// the same component, so without it they would be one number.

export const trackFormView = (form, params = {}) =>
  track(EVENTS.formView, { form_name: form, ...params });

export const trackFormStart = (form, params = {}) =>
  track(EVENTS.formStart, { form_name: form, ...params });

export const trackFormItems = (form) => track(EVENTS.formItems, { form_name: form });

export const trackFormStep = (form, step, stepName) =>
  track(EVENTS.formStep, { form_name: form, step, step_name: stepName });

// Submitted, and accepted by the server. `order_id` lets Analytics drop a repeat
// of the same lead, such as a refresh of the confirmation page.
export const trackLead = (form, params = {}) =>
  track(EVENTS.lead, { form_name: form, ...params });

export const trackToolUsed = (tool, params = {}) =>
  track(EVENTS.toolUsed, { tool_name: tool, ...params });

export const trackPriceAlert = (params = {}) => track(EVENTS.priceAlert, params);

export const trackViewPrices = (params = {}) => track(EVENTS.viewPrices, params);

export const trackViewGuide = (slug, category) =>
  track(EVENTS.viewGuide, { slug, category });

// A tap on a telephone number, which is not the same thing as a call. It is the
// best signal a browser can give us and it over-counts: a mis-tap fires it, and
// a number copied and dialled from a landline fires nothing. The playbook only
// counts a call as a conversion at 60 seconds or longer, and that figure comes
// from the call reporting in Google Ads rather than from here.
export const trackPhoneCall = (where) => track(EVENTS.phoneCall, { where });

export const trackContact = (params = {}) => track(EVENTS.contact, params);

export const trackRegisterShow = (show, people) =>
  track(EVENTS.registerShow, { show, people });

// No amount on it, and that is deliberate rather than an omission. Section 7 of
// the privacy policy says the tags never receive any figure from an offer we
// make, and that what we paid somebody is never shared with anyone for any
// purpose. The order number is enough to count the sale and to join it to the
// order by hand; the figure stays here.
export const trackOfferAccepted = (orderId) =>
  track(EVENTS.offerAccepted, { order_id: orderId });

export const trackPayoutSubmitted = (method) =>
  track(EVENTS.payoutSubmitted, { method });

export const trackCoverageRequested = (params = {}) =>
  track(EVENTS.coverageRequested, params);

export const trackAffiliateApplied = (params = {}) =>
  track(EVENTS.affiliateApplied, params);

// One visit, one profile, named by the order it became. The id is the order
// number and nothing else: no name, no email, no telephone number. That is what
// lets you open a profile in OpenPanel, see the whole path from the first page
// view to the submit, and then look the person up in the dashboard by the same
// number. The analytics never learns who they are.
export const identify = (orderNumber) =>
  toPanel((panel) => panel.identify({ profileId: String(orderNumber) }));

////////// 4. WHERE THEY CAME FROM //////////
// Read once on arrival and kept for the session, because somebody reads three
// articles before they fill the form in and the campaign parameters are long
// gone off the URL by then. This is stored on the order and read by the
// dashboard; it is never sent to an ad platform by us.

function stashArrival() {
  try {
    if (sessionStorage.getItem(STASH_KEY)) return;
    const url = new URL(window.location.href);
    const pick = (key) => url.searchParams.get(key) || undefined;
    sessionStorage.setItem(STASH_KEY, JSON.stringify({
      gclid: pick("gclid"),
      utmSource: pick("utm_source"),
      utmMedium: pick("utm_medium"),
      utmCampaign: pick("utm_campaign"),
      landingPath: url.pathname,
      referrer: document.referrer || undefined,
    }));
  } catch { /* storage blocked, attribution is simply thinner */ }
}

// Called once from the layout, on the first render of any page. The layout only
// renders on the customer site, which is what keeps the dashboard out of this.
export function init() {
  if (!browser() || !allowed()) return;
  stashArrival();
  startPanel();
}

// Everything the kit form posts alongside the request. Empty when the visitor
// has opted out, and the form does not care either way.
export function context() {
  if (!allowed()) return {};
  try {
    // deviceId joins this browser's visit to the events the server sends later,
    // and it is stored on the order by server/src/lib/attribution.js.
    return { ...JSON.parse(sessionStorage.getItem(STASH_KEY) || "{}"), deviceId: deviceId() };
  } catch {
    return { deviceId: deviceId() };
  }
}

////////// 5. THE OPT OUT //////////
// What the "Do not share my information" control on the privacy page calls.
// Opting out cannot unsend what has already gone, and the copy there says so.
//
// Consent is only ever withdrawn from here, never granted. An update ignores
// region, so granting from JavaScript would quietly override the European
// denial set in index.html and hand consent to somebody who never gave it.

const CONSENT_KEYS = ["ad_storage", "ad_user_data", "ad_personalization", "analytics_storage"];

export function optOut() {
  try { localStorage.setItem(OPT_OUT_KEY, "1"); } catch { /* nothing to store into */ }
  // Withdraw before reloading, so the decision reaches a tag that is already
  // configured rather than waiting on the reload. After the reload the inline
  // check in index.html means the tag is never configured at all.
  window.gtag?.("consent", "update", Object.fromEntries(CONSENT_KEYS.map((key) => [key, "denied"])));
  if (browser()) window.location.reload();
}

export function optIn() {
  try { localStorage.removeItem(OPT_OUT_KEY); } catch { /* nothing to remove from */ }
  if (browser()) window.location.reload();
}

export function optedOut() {
  if (!browser()) return false;
  if (signalsOptOut()) return true;
  try { return localStorage.getItem(OPT_OUT_KEY) === "1"; } catch { return false; }
}

export const gpcActive = () => browser() && navigator.globalPrivacyControl === true;

////////// 6. OPENPANEL //////////
// Our own analytics, cookieless, and the browser only ever talks to this site:
// the SDK posts to /api/pulse and the server forwards it, so a blocker that
// refuses analytics hosts does not quietly lose the visit. See
// server/src/lib/openpanel.js.
//
// It is imported here rather than in index.html on purpose. It has no job before
// the app is running, and keeping it out of the HTML means it cannot slow the
// first paint of a prerendered page.
//
// Session replay is on, because watching where somebody stalls on the kit form
// is the one thing no event can tell you. Two protections make that safe to say.
//
// Every input value is masked, so a recording of the form shows the typing
// without the typed. And the two pages that render somebody's details as text
// rather than into a box are never recorded at all, whatever the rate says: the
// tracking page, which shows a customer their own name, address, offer and the
// payout form inside it, and the affiliate's page, which shows their earnings.
// Masking inputs would not help on either, because none of it is an input.
//
// The one leak that masking misses on the form itself is the address
// suggestion list, which renders a real street address as text. It carries
// data-private, which blocks it out of the recording.

const PANEL_ID = import.meta.env.VITE_OPENPANEL_CLIENT_ID;
// The relay lives on the API, which is a different origin in production, so the
// base has to come from the same variable api.js reads. A relative "/api/pulse"
// posts to the static site instead and 404s on every event, silently.
const apiBase = import.meta.env.VITE_API_URL || "";
const PANEL_URL = import.meta.env.VITE_OPENPANEL_API_URL
  || `${apiBase.endsWith("/") ? apiBase.slice(0, -1) : apiBase}/api/pulse`;
const REPLAY_RATE = Number(import.meta.env.VITE_OPENPANEL_REPLAY_RATE ?? 1);
const NEVER_RECORD = ["/track", "/affiliate-portal"];

let panel = null;
const waiting = [];

// Queued rather than dropped, because the first events of a visit fire while the
// SDK is still downloading.
function toPanel(run) {
  if (!PANEL_ID) return;
  if (panel) run(panel);
  else waiting.push(run);
}

async function startPanel() {
  if (!PANEL_ID) {
    if (import.meta.env.DEV) {
      console.info("[analytics] VITE_OPENPANEL_CLIENT_ID is not set, so only the Google tag is measuring.");
    }
    return;
  }
  if (panel) return;
  const recordable = REPLAY_RATE > 0
    && !NEVER_RECORD.some((path) => window.location.pathname.startsWith(path));

  const { OpenPanel } = await import("@openpanel/web");
  panel = new OpenPanel({
    clientId: PANEL_ID,
    apiUrl: PANEL_URL,
    // Route changes and outgoing links counted without being asked, and
    // data-track="name" on any element makes it measurable with no code. Other
    // data-* attributes on that element come along as properties.
    trackScreenViews: true,
    trackOutgoingLinks: true,
    trackAttributes: true,
    sessionReplay: recordable
      ? { enabled: true, sampleRate: REPLAY_RATE, maskAllInputs: true, blockSelector: "[data-private]" }
      : { enabled: false },
  });
  waiting.splice(0).forEach((run) => run(panel));
}

// This browser's analytics id. The kit form posts it with the request so the
// server can put a parcel arriving weeks later on the visit that caused it.
export function deviceId() {
  try { return panel?.getDeviceId() || undefined; } catch { return undefined; }
}
